import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Badge } from "../../../../components/ui/badge";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    Legend,
} from "recharts";
import {
    ArrowLeft,
    AlertTriangle,
    Clock,
    Activity,
    ChevronRight,
    Layers,
} from "lucide-react";
import moment from "moment";
import {
    useGetDevice,
    useGetDeviceDataAroundError,
    useGetNearbyErrors,
    useGetDeviceConfigAttributes,
} from "../../../../api/routes/devices";
import Header from "../header";
import { Button } from "../../../../components/ui/button";
import { ScrollArea } from "../../../../components/ui/scroll-area";

const METRIC_COLORS = [
    "hsl(220, 70%, 55%)",
    "hsl(150, 60%, 45%)",
    "hsl(35, 80%, 55%)",
    "hsl(270, 60%, 55%)",
    "hsl(180, 55%, 45%)",
    "hsl(330, 60%, 55%)",
    "hsl(60, 70%, 45%)",
    "hsl(200, 65%, 50%)",
    "hsl(100, 55%, 45%)",
    "hsl(10, 70%, 50%)",
];

const EXCLUDED_PREFIXES = ["Is", "Error", "Productivity"];
const shouldExcludeMetric = (key: string) =>
    EXCLUDED_PREFIXES.some((prefix) => key.startsWith(prefix));

const formatTime = (ts: number) => moment(ts).format("HH:mm:ss");

type MetricDataPoint = {
    timestamp: number;
    [key: string]: number | undefined;
};

type NearbyErrorEntry = {
    deviceData: { id: string; timestamp: string; value: string };
    error: { code: string; title: string; description: string; number: number } | null;
};

// Renders a red diamond marker at the top of the chart for the main error
const MainErrorMarker = ({ viewBox, code }: { viewBox?: { x: number }; code: string }) => {
    if (!viewBox) return null;
    const { x } = viewBox;
    const y = 6;
    const size = 7;
    return (
        <g style={{ cursor: "pointer" }}>
            <title>{`This Error: ${code}`}</title>
            <polygon
                points={`${x},${y - size} ${x + size},${y} ${x},${y + size} ${x - size},${y}`}
                fill="hsl(0, 80%, 50%)"
                stroke="white"
                strokeWidth={1.5}
            />
        </g>
    );
};

// Renders an orange circle marker at the top of the chart for a nearby error
const NearbyErrorMarker = ({ viewBox, errorEntry }: { viewBox?: { x: number }; errorEntry: NearbyErrorEntry }) => {
    if (!viewBox || !errorEntry.error) return null;
    const { x } = viewBox;
    const y = 6;
    return (
        <g style={{ cursor: "pointer" }}>
            <title>{`${errorEntry.error.code}: ${errorEntry.error.title}`}</title>
            <circle cx={x} cy={y} r={6} fill="hsl(30, 90%, 55%)" stroke="white" strokeWidth={1.5} />
            <text x={x} y={y + 3.5} textAnchor="middle" fontSize={8} fill="white" fontWeight={700}>
                !
            </text>
        </g>
    );
};

// Chart margins - shared between chart and slider for alignment
const CHART_MARGIN = { top: 24, right: 10, left: 0, bottom: 5 };
const YAXIS_WIDTH = 50;

const ErrorDetailRoute = () => {
    const { id: deviceId, errorId } = useParams();
    const { data: device, isLoading: deviceLoading } = useGetDevice(deviceId as string);
    const { data: contextData, isLoading: dataLoading } = useGetDeviceDataAroundError(
        deviceId as string,
        errorId as string
    );
    const { data: nearbyErrors, isLoading: nearbyLoading } = useGetNearbyErrors(
        deviceId as string,
        errorId as string
    );
    const { data: attributes } = useGetDeviceConfigAttributes(deviceId as string);

    // Build a map from attribute key name to friendly display name
    const displayNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        if (attributes) {
            attributes.forEach((attr: { name: string; friendlyName?: string }) => {
                if (attr.friendlyName) {
                    map[attr.name] = attr.friendlyName;
                }
            });
        }
        return map;
    }, [attributes]);

    const getDisplayName = (key: string) => displayNameMap[key] || key;

    const [visibleMetrics, setVisibleMetrics] = useState<Set<string>>(new Set());
    const [initialized, setInitialized] = useState(false);
    const [sliderIndex, setSliderIndex] = useState<number | null>(null);
    const errorTimestamp = contextData
        ? parseInt(contextData.error.deviceData.timestamp)
        : 0;

    // Nearby errors that fall within the 5-minute chart window
    const nearbyErrorsOnChart = useMemo(() => {
        if (!nearbyErrors || !errorTimestamp) return [];
        const windowMs = 5 * 60 * 1000;
        return nearbyErrors.filter((ne: NearbyErrorEntry) => {
            if (!ne.error) return false;
            const ts = parseInt(ne.deviceData.timestamp);
            return ts >= errorTimestamp - windowMs && ts <= errorTimestamp + windowMs;
        });
    }, [nearbyErrors, errorTimestamp]);

    // Transform raw data into chart-ready format grouped by timestamp
    const { chartData, metrics, metricStats } = useMemo(() => {
        if (!contextData) return { chartData: [], metrics: [], metricStats: {} };

        const { before, after } = contextData;
        const combined = [...before, ...after].sort(
            (a: Record<string, string>, b: Record<string, string>) =>
                parseInt(a.timestamp) - parseInt(b.timestamp)
        );

        // Get unique metric keys, filtering out excluded ones
        const metricSet = new Set(
            combined
                .map((d: Record<string, string>) => d.key)
                .filter((key: string) => !shouldExcludeMetric(key))
        );
        const metricList = Array.from(metricSet) as string[];

        // Group data by timestamp, pivot metrics into columns
        const timeMap = new Map<number, MetricDataPoint>();
        combined.forEach((d: Record<string, string>) => {
            if (shouldExcludeMetric(d.key)) return;
            const ts = parseInt(d.timestamp);
            if (!timeMap.has(ts)) {
                timeMap.set(ts, { timestamp: ts });
            }
            const point = timeMap.get(ts)!;
            point[d.key] = parseFloat(d.value);
        });

        const sortedData = Array.from(timeMap.values()).sort(
            (a, b) => a.timestamp - b.timestamp
        );

        // Calculate stats per metric
        const stats: Record<string, { min: number; max: number; avg: number; atError: number | null }> = {};
        const errorTs = parseInt(contextData.error.deviceData.timestamp);

        metricList.forEach((metric) => {
            const values = combined
                .filter((d: Record<string, string>) => d.key === metric)
                .map((d: Record<string, string>) => parseFloat(d.value))
                .filter((v: number) => !isNaN(v));

            const atErrorPoint = combined
                .filter((d: Record<string, string>) => d.key === metric)
                .reduce(
                    (closest: Record<string, string> | null, d: Record<string, string>) => {
                        if (!closest) return d;
                        return Math.abs(parseInt(d.timestamp) - errorTs) <
                            Math.abs(parseInt(closest.timestamp) - errorTs)
                            ? d
                            : closest;
                    },
                    null
                );

            stats[metric] = {
                min: values.length > 0 ? Math.min(...values) : 0,
                max: values.length > 0 ? Math.max(...values) : 0,
                avg: values.length > 0 ? values.reduce((a: number, b: number) => a + b, 0) / values.length : 0,
                atError: atErrorPoint ? parseFloat(atErrorPoint.value) : null,
            };
        });

        // Initialize visible metrics on first load
        if (!initialized && metricList.length > 0) {
            setVisibleMetrics(new Set(metricList.slice(0, 5)));
            setInitialized(true);
        }

        return { chartData: sortedData, metrics: metricList, metricStats: stats };
    }, [contextData, initialized]);

    // Get values at the current slider position by finding the last known value for each metric
    const valuesAtSlider = useMemo(() => {
        if (sliderIndex === null || chartData.length === 0) return null;
        const targetPoint = chartData[sliderIndex];
        if (!targetPoint) return null;

        const result: Record<string, number | null> = {};

        metrics.forEach((metric) => {
            let lastValue: number | null = null;
            for (let i = sliderIndex; i >= 0; i--) {
                const val = chartData[i][metric];
                if (val !== undefined && val !== null && typeof val === "number") {
                    lastValue = val;
                    break;
                }
            }
            result[metric] = lastValue;
        });

        return { timestamp: targetPoint.timestamp, values: result };
    }, [sliderIndex, chartData, metrics]);

    const toggleMetric = (metric: string) => {
        setVisibleMetrics((prev) => {
            const next = new Set(prev);
            if (next.has(metric)) {
                next.delete(metric);
            } else {
                next.add(metric);
            }
            return next;
        });
    };

    // Custom chart tooltip - shows error info when hovering near error timestamps
    const CustomTooltip = ({
        active,
        payload,
        label,
    }: {
        active?: boolean;
        payload?: Array<{ name: string; value: number; color: string }>;
        label?: number;
    }) => {
        if (!active || !payload || !payload.length || label === undefined) return null;

        const hoveredTs = label;
        const tolerance = 2000; // 2 second tolerance for matching

        // Check if hovering near the main error's timestamp
        const isMainError = Math.abs(hoveredTs - errorTimestamp) < tolerance;

        // Check if any nearby errors are near this timestamp
        const matchingNearby = nearbyErrorsOnChart.filter(
            (ne: NearbyErrorEntry) => Math.abs(parseInt(ne.deviceData.timestamp) - hoveredTs) < tolerance
        );

        const metricEntries = payload.filter((entry) => entry.value != null);

        return (
            <div className="bg-popover text-popover-foreground p-3 rounded-lg shadow-lg border text-sm max-w-xs">
                <p className="font-medium mb-1">{formatTime(hoveredTs)}</p>

                {/* Main error callout */}
                {isMainError && contextData && (
                    <div className="mb-2 pb-2 border-b border-border">
                        <div className="flex items-center gap-1.5 text-xs">
                            <span className="flex-shrink-0" style={{ color: "hsl(0, 80%, 50%)" }}>&#9670;</span>
                            <span className="font-semibold text-red-600 dark:text-red-400">
                                {contextData.error.error?.code}: {contextData.error.error?.title}
                            </span>
                        </div>
                    </div>
                )}

                {/* Nearby errors callout */}
                {matchingNearby.length > 0 && (
                    <div className="mb-2 pb-2 border-b border-border">
                        {matchingNearby.map((ne: NearbyErrorEntry) => (
                            <div key={ne.deviceData.id} className="flex items-center gap-1.5 text-xs">
                                <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
                                <span className="font-medium text-orange-600 dark:text-orange-400">
                                    {ne.error?.code}: {ne.error?.title}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Metric values */}
                {metricEntries.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2">
                        <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-muted-foreground">{getDisplayName(entry.name)}:</span>
                        <span className="font-mono font-medium">
                            {typeof entry.value === "number" ? entry.value.toFixed(2) : entry.value}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    if (deviceLoading || dataLoading) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 w-48 bg-muted rounded" />
                    <div className="h-10 w-full sm:w-96 bg-muted rounded-lg" />
                    <div className="h-32 bg-muted rounded-lg" />
                    <div className="h-96 bg-muted rounded-lg" />
                </div>
            </div>
        );
    }

    if (!device || !deviceId || !contextData) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <Header deviceId={deviceId || ""} name="" businessId={device?.business?.id} />
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <AlertTriangle className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-medium text-foreground mb-1">Error Data Not Found</p>
                    <p className="text-xs text-muted-foreground">
                        Could not load error context data. The error may have been removed.
                    </p>
                    <Link
                        to={`/devices/${deviceId}/errors`}
                        className="inline-flex items-center text-sm text-foreground hover:underline mt-4"
                    >
                        <ArrowLeft className="mr-1 h-3 w-3" /> Back to Errors
                    </Link>
                </div>
            </div>
        );
    }

    const { error } = contextData;

    // Determine which data the "Values" section shows
    const isSliderActive = sliderIndex !== null;
    const displayValues = isSliderActive && valuesAtSlider
        ? valuesAtSlider.values
        : Object.fromEntries(
              metrics.map((m) => [m, metricStats[m]?.atError ?? null])
          );
    const displayTimestamp = isSliderActive && valuesAtSlider
        ? valuesAtSlider.timestamp
        : errorTimestamp;

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <Header deviceId={deviceId} name={device?.name} businessId={device?.business?.id} />

            {/* Back link */}
            <Link
                to={`/devices/${deviceId}/errors`}
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
            >
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back to Errors
            </Link>

            <div className="space-y-6">
                {/* Error Summary */}
                <div>
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Error Details
                    </h2>
                    <div className="rounded-lg border bg-card p-4 sm:p-5">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Badge variant="destructive" className="h-6 text-xs">
                                        {error.error?.code}
                                    </Badge>
                                    <Badge variant="outline" className="h-6 text-xs">
                                        <Clock className="mr-1 h-3 w-3" />
                                        {moment(errorTimestamp).format("MMM D, YYYY h:mm:ss A")}
                                    </Badge>
                                </div>
                                <h3 className="text-lg font-semibold text-foreground mb-1">
                                    {error.error?.title}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                    {error.error?.description}
                                </p>
                            </div>
                            <div className="flex-shrink-0 text-right">
                                <p className="text-xs text-muted-foreground">
                                    {moment(errorTimestamp).fromNow()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Device Stats Around Error */}
                <div>
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Device Stats Around Error
                    </h2>

                    {metrics.length > 0 ? (
                        <div className="space-y-3">
                            {/* Metric Toggles */}
                            <div className="rounded-lg border bg-card p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                        Metrics ({visibleMetrics.size} of {metrics.length} shown)
                                    </p>
                                    <div className="flex gap-1">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 text-xs px-2"
                                            onClick={() => setVisibleMetrics(new Set(metrics))}
                                        >
                                            All
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 text-xs px-2"
                                            onClick={() => setVisibleMetrics(new Set())}
                                        >
                                            None
                                        </Button>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {metrics.map((metric, index) => (
                                        <button
                                            key={metric}
                                            onClick={() => toggleMetric(metric)}
                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors border ${
                                                visibleMetrics.has(metric)
                                                    ? "bg-foreground/5 border-foreground/20 text-foreground"
                                                    : "bg-transparent border-border text-muted-foreground hover:border-foreground/20"
                                            }`}
                                        >
                                            <span
                                                className="w-2 h-2 rounded-full flex-shrink-0"
                                                style={{
                                                    backgroundColor: visibleMetrics.has(metric)
                                                        ? METRIC_COLORS[index % METRIC_COLORS.length]
                                                        : "transparent",
                                                    border: visibleMetrics.has(metric)
                                                        ? "none"
                                                        : "1px solid currentColor",
                                                }}
                                            />
                                            {getDisplayName(metric)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Chart */}
                            {visibleMetrics.size > 0 && chartData.length > 0 && (
                                <div className="rounded-lg border bg-card p-4 sm:p-5">
                                    <div className="flex items-center justify-between mb-4">
                                        <p className="text-sm font-medium text-foreground">
                                            5-Minute Window
                                        </p>
                                        <div className="flex items-center gap-2">
                                            {nearbyErrorsOnChart.length > 0 && (
                                                <Badge variant="outline" className="h-5 text-xs bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-400 dark:border-orange-800">
                                                    {nearbyErrorsOnChart.length} nearby error{nearbyErrorsOnChart.length !== 1 ? "s" : ""}
                                                </Badge>
                                            )}
                                            <Badge variant="outline" className="h-5 text-xs">
                                                <Activity className="mr-1 h-3 w-3" />
                                                {chartData.length} data points
                                            </Badge>
                                        </div>
                                    </div>
                                    <div className="h-80 sm:h-96">
                                        <ResponsiveContainer>
                                            <LineChart
                                                data={chartData}
                                                margin={CHART_MARGIN}
                                            >
                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                    className="opacity-30"
                                                />
                                                <XAxis
                                                    dataKey="timestamp"
                                                    type="number"
                                                    domain={["dataMin", "dataMax"]}
                                                    tickFormatter={(ts) => formatTime(ts)}
                                                    tick={{ fontSize: 11 }}
                                                    tickLine={false}
                                                    scale="time"
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 11 }}
                                                    tickLine={false}
                                                    width={YAXIS_WIDTH}
                                                />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend
                                                    wrapperStyle={{ fontSize: "12px" }}
                                                    payload={[
                                                        ...metrics
                                                            .filter((m) => visibleMetrics.has(m))
                                                            .map((m) => ({
                                                                value: getDisplayName(m),
                                                                type: "line" as const,
                                                                color: METRIC_COLORS[metrics.indexOf(m) % METRIC_COLORS.length],
                                                            })),
                                                        { value: "This Error", type: "diamond" as const, color: "hsl(0, 80%, 50%)" },
                                                        ...(nearbyErrorsOnChart.length > 0
                                                            ? [{ value: "Nearby Errors", type: "circle" as const, color: "hsl(30, 90%, 55%)" }]
                                                            : []),
                                                    ]}
                                                />

                                                {/* Metric lines */}
                                                {metrics.map((metric, index) =>
                                                    visibleMetrics.has(metric) ? (
                                                        <Line
                                                            key={metric}
                                                            type="monotone"
                                                            dataKey={metric}
                                                            name={getDisplayName(metric)}
                                                            stroke={METRIC_COLORS[index % METRIC_COLORS.length]}
                                                            strokeWidth={2}
                                                            dot={false}
                                                            activeDot={{ r: 4 }}
                                                            connectNulls
                                                        />
                                                    ) : null
                                                )}

                                                {/* Main error - red dashed line with diamond marker */}
                                                <ReferenceLine
                                                    x={errorTimestamp}
                                                    stroke="hsl(0, 80%, 50%)"
                                                    strokeWidth={2}
                                                    strokeDasharray="6 3"
                                                    label={(props: { viewBox?: { x: number } }) => (
                                                        <MainErrorMarker viewBox={props.viewBox} code={error.error?.code || "ERR"} />
                                                    )}
                                                />

                                                {/* Nearby error markers - orange dashed lines with circle markers */}
                                                {nearbyErrorsOnChart.map((ne: NearbyErrorEntry) => {
                                                    const neTs = parseInt(ne.deviceData.timestamp);
                                                    if (neTs === errorTimestamp) return null;
                                                    return (
                                                        <ReferenceLine
                                                            key={ne.deviceData.id}
                                                            x={neTs}
                                                            stroke="hsl(30, 90%, 55%)"
                                                            strokeDasharray="4 4"
                                                            strokeWidth={1.5}
                                                            strokeOpacity={0.7}
                                                            label={(props: { viewBox?: { x: number } }) => (
                                                                <NearbyErrorMarker viewBox={props.viewBox} errorEntry={ne} />
                                                            )}
                                                        />
                                                    );
                                                })}

                                                {/* Slider position reference line */}
                                                {isSliderActive && chartData[sliderIndex] && (
                                                    <ReferenceLine
                                                        x={chartData[sliderIndex].timestamp}
                                                        stroke="hsl(220, 70%, 55%)"
                                                        strokeDasharray="2 2"
                                                        strokeWidth={1}
                                                    />
                                                )}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Time Slider - padded to align with chart plot area */}
                                    <div className="mt-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-muted-foreground whitespace-nowrap font-mono">
                                                {chartData.length > 0 ? formatTime(chartData[0].timestamp) : ""}
                                            </span>
                                            <div className="flex-1 relative" style={{ marginLeft: CHART_MARGIN.left, marginRight: CHART_MARGIN.right }}>
                                                <input
                                                    type="range"
                                                    min={0}
                                                    max={chartData.length - 1}
                                                    value={sliderIndex ?? Math.floor(chartData.length / 2)}
                                                    onChange={(e) => setSliderIndex(parseInt(e.target.value))}
                                                    className="w-full h-1.5 bg-muted rounded-full appearance-none cursor-pointer accent-foreground [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-foreground [&::-webkit-slider-thumb]:cursor-pointer"
                                                />
                                            </div>
                                            <span className="text-xs text-muted-foreground whitespace-nowrap font-mono">
                                                {chartData.length > 0 ? formatTime(chartData[chartData.length - 1].timestamp) : ""}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between mt-2">
                                            <div className="flex items-center gap-2">
                                                {isSliderActive && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-6 text-xs px-2"
                                                        onClick={() => setSliderIndex(null)}
                                                    >
                                                        Reset to error time
                                                    </Button>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                {isSliderActive && chartData[sliderIndex]
                                                    ? `Viewing: ${moment(chartData[sliderIndex].timestamp).format("h:mm:ss A")}`
                                                    : "Drag slider to inspect values at any point"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Metric Values - responds to slider */}
                            <div className="rounded-lg border bg-card p-4 sm:p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                        {isSliderActive ? "Values at Selected Time" : "Values at Error Time"}
                                    </p>
                                    <Badge variant="outline" className="h-5 text-xs">
                                        <Clock className="mr-1 h-3 w-3" />
                                        {moment(displayTimestamp).format("h:mm:ss A")}
                                    </Badge>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {metrics.map((metric, index) => {
                                        const currentVal = displayValues[metric];
                                        return (
                                            <div
                                                key={metric}
                                                className="rounded-md border bg-background p-3"
                                            >
                                                <div className="flex items-center gap-1.5 mb-1">
                                                    <span
                                                        className="w-2 h-2 rounded-full flex-shrink-0"
                                                        style={{
                                                            backgroundColor:
                                                                METRIC_COLORS[index % METRIC_COLORS.length],
                                                        }}
                                                    />
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        {getDisplayName(metric)}
                                                    </p>
                                                </div>
                                                <p className="text-sm font-semibold text-foreground font-mono">
                                                    {currentVal !== null && currentVal !== undefined
                                                        ? (typeof currentVal === "number" ? currentVal.toFixed(2) : currentVal)
                                                        : "N/A"}
                                                </p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-[10px] text-muted-foreground">
                                                        min {metricStats[metric]?.min.toFixed(1)}
                                                    </span>
                                                    <span className="text-[10px] text-muted-foreground">
                                                        max {metricStats[metric]?.max.toFixed(1)}
                                                    </span>
                                                    <span className="text-[10px] text-muted-foreground">
                                                        avg {metricStats[metric]?.avg.toFixed(1)}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-lg border border-dashed p-8 text-center">
                            <Activity className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">
                                No device data recorded around this error.
                            </p>
                        </div>
                    )}
                </div>

                {/* Nearby Errors */}
                <div>
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Nearby Errors
                        <span className="ml-2 text-xs font-normal normal-case">
                            (within 1 hour)
                        </span>
                    </h2>
                    {nearbyLoading ? (
                        <div className="rounded-lg border bg-card p-4 animate-pulse">
                            <div className="h-16 bg-muted rounded" />
                        </div>
                    ) : nearbyErrors && nearbyErrors.length > 0 ? (
                        <div className="rounded-lg border bg-card p-4 sm:p-5">
                            <div className="flex items-center gap-2 mb-3">
                                <Layers className="h-4 w-4 text-muted-foreground" />
                                <p className="text-xs text-muted-foreground">
                                    {nearbyErrors.length} other error{nearbyErrors.length !== 1 ? "s" : ""} occurred nearby
                                </p>
                            </div>
                            <ScrollArea className={nearbyErrors.length > 5 ? "h-[300px]" : ""}>
                                {nearbyErrors.map((nearby: NearbyErrorEntry) => {
                                    if (!nearby.error) return null;
                                    const ts = parseInt(nearby.deviceData.timestamp);
                                    const diffMs = ts - errorTimestamp;
                                    const diffMinutes = Math.round(diffMs / 60000);
                                    const diffLabel =
                                        diffMinutes === 0
                                            ? "same time"
                                            : diffMinutes > 0
                                              ? `+${diffMinutes}m after`
                                              : `${diffMinutes}m before`;

                                    const isOnChart = nearbyErrorsOnChart.some(
                                        (ne: NearbyErrorEntry) => ne.deviceData.id === nearby.deviceData.id
                                    );

                                    return (
                                        <Link
                                            key={nearby.deviceData.id}
                                            to={`/devices/${deviceId}/errors/${nearby.deviceData.id}`}
                                        >
                                            <div
                                                className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0 hover:bg-muted/50 -mx-4 px-4 transition-colors cursor-pointer group"
                                            >
                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                    {isOnChart && (
                                                        <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
                                                    )}
                                                    <Badge
                                                        variant="destructive"
                                                        className="h-5 text-xs flex-shrink-0"
                                                    >
                                                        {nearby.error.code}
                                                    </Badge>
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium text-foreground truncate">
                                                            {nearby.error.title}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {moment(ts).format("h:mm:ss A")}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    {isOnChart && (
                                                        <span className="text-[10px] text-orange-600 dark:text-orange-400">
                                                            on chart
                                                        </span>
                                                    )}
                                                    <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${
                                                        diffMinutes === 0
                                                            ? "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400"
                                                            : "bg-muted text-muted-foreground"
                                                    }`}>
                                                        {diffLabel}
                                                    </span>
                                                    <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                                </div>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </ScrollArea>
                        </div>
                    ) : (
                        <div className="rounded-lg border border-dashed p-8 text-center">
                            <p className="text-sm text-muted-foreground">
                                No other errors occurred within 1 hour of this error.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export { ErrorDetailRoute };
